import { useEffect, useState, useCallback, useRef } from "react";
import { X as CloseIcon, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Reusable photo lightbox / carousel with:
 *   - prev/next arrow buttons
 *   - keyboard navigation (← / → / Escape)
 *   - swipe gestures on touch devices
 *   - optional "Save photo" button (Web Share on mobile, blob download fallback)
 *   - position indicator (e.g. "3 / 7")
 *
 * Props:
 *   photos: [{ id, url, caption?, original_name?, filename? }]
 *   index: number | null   — current index; null = closed
 *   onClose: () => void
 *   onIndexChange: (n) => void
 *   showSave: boolean      — defaults to true
 */
export default function PhotoLightbox({ photos = [], index, onClose, onIndexChange, showSave = true }) {
  const touch = useRef({ startX: 0, startY: 0, active: false });
  const [saving, setSaving] = useState(false);
  const open = index !== null && index !== undefined && photos[index];

  const goTo = useCallback((delta) => {
    if (!open) return;
    const next = (index + delta + photos.length) % photos.length;
    onIndexChange(next);
  }, [index, photos.length, onIndexChange, open]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") { e.preventDefault(); goTo(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); goTo(-1); }
      else if (e.key === "Escape") { e.preventDefault(); onClose(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, goTo, onClose]);

  // Lock scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  const photo = photos[index];

  // ---- Swipe handlers ----
  const onTouchStart = (e) => {
    const t = e.touches[0];
    touch.current = { startX: t.clientX, startY: t.clientY, active: true };
  };
  const onTouchEnd = (e) => {
    if (!touch.current.active) return;
    touch.current.active = false;
    const t = e.changedTouches[0];
    const dx = t.clientX - touch.current.startX;
    const dy = t.clientY - touch.current.startY;
    // Horizontal swipe wins only if it's clearly horizontal & long enough
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      goTo(dx < 0 ? 1 : -1);
    }
  };

  // ---- Save handler (Web Share → download fallback) ----
  const savePhoto = async () => {
    setSaving(true);
    try {
      const filename = photo.original_name || photo.filename || "photo.jpg";
      const res = await fetch(photo.url, { credentials: "omit" });
      if (!res.ok) throw new Error("fetch failed");
      const blob = await res.blob();
      const file = new File([blob], filename, { type: blob.type || "image/jpeg" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: filename });
          return;
        } catch (err) {
          if (err && err.name === "AbortError") return;
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch {
      window.open(photo.url, "_blank", "noopener,noreferrer");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center select-none"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="photo-lightbox"
    >
      {/* Close */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-3 right-3 z-10 text-white/90 hover:text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
        aria-label="Close"
        data-testid="lightbox-close"
      >
        <CloseIcon className="w-5 h-5" />
      </button>

      {/* Position indicator */}
      {photos.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/80 text-xs font-semibold tracking-wider bg-white/10 rounded-full px-3 py-1">
          {index + 1} / {photos.length}
        </div>
      )}

      {/* Prev arrow */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goTo(-1); }}
          className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-10 text-white/90 hover:text-white p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Previous"
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}

      {/* Image + caption + save */}
      <div
        className="relative max-w-5xl w-full max-h-[92vh] flex flex-col items-center px-3"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={photo.url}
          alt={photo.caption || photo.original_name || "Photo"}
          className="max-h-[78vh] w-auto object-contain rounded-lg shadow-2xl"
          draggable={false}
        />
        {photo.caption && (
          <p className="text-sm text-white/90 mt-3 text-center max-w-2xl px-4">{photo.caption}</p>
        )}
        {showSave && (
          <div className="flex flex-col items-center mt-3">
            <button
              type="button"
              onClick={savePhoto}
              disabled={saving}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-white text-slate-800 hover:bg-slate-100 text-sm font-semibold disabled:opacity-60"
              data-testid="lightbox-save"
            >
              <Download className="w-4 h-4" />
              {saving ? "Saving…" : "Save photo"}
            </button>
            <p className="mt-2 text-[11px] text-white/60 text-center max-w-xs">
              On iPhone / Android, tap Save → choose <b>Save Image</b> to add to your Photos.
            </p>
          </div>
        )}
      </div>

      {/* Next arrow */}
      {photos.length > 1 && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); goTo(1); }}
          className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-10 text-white/90 hover:text-white p-2 sm:p-3 rounded-full bg-white/10 hover:bg-white/20"
          aria-label="Next"
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>
      )}
    </div>
  );
}
